<template>
  <div v-if="showComponent">
    <div v-if="editor">
      <label
        v-if="label"
        :for="idComputed"
        :title="titleComputed"
        :class=" {'col-sm-2': isOnModeLabelOnLeft, 'col-form-label': isOnModeLabelOnLeft }"
        class="text-truncate"
      >
        {{label}}
        <small v-if="required">*</small>
      </label>
      <font-awesome-icon
        id="font-awesome-icon-edit"
        icon="edit"
        size="sm"
        class="clickable float-right mt-1"
        :class="{ 'ml-2': help }"
        v-if="showEditButton"
        @click="$emit('edit-button-clicked')"
      />
      <font-awesome-icon
        icon="question-circle"
        size="sm"
        class="clickable float-right mt-1"
        v-if="help"
        v-b-tooltip.hover="help"
      />

      <template v-if="!disabled && !hideMenus">
        <TextMenu
          :editor="editor"
          :hasVariables="!!variablesOptions.length"
          @toggle-table-settings="toggleTableSettings = !toggleTableSettings"
          @toggle-variables="toggleVariables = !toggleVariables"
        />

        <TableMenu :editor="editor" v-if="toggleTableSettings" />

        <LmTreeSelect
          class="mt-3"
          v-if="variablesOptions.length && toggleVariables"
          :options="variablesOptions"
          placeholder="Insertar variable"
          @input="onInsertVariable"
        />
      </template>
      <editor-content :editor="editor" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Mention from '@tiptap/extension-mention'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'

// Components
import suggestion from './utils/suggestion'
import TableMenu from './components/TableMenu'
import TextMenu from './components/TextMenu'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'

// Utils
import lmInputMixin from '@/mixins/lmInputMixin.js'

const CustomTableCell = TableCell.extend({
  addAttributes () {
    return {
      // extend the existing attributes …
      ...this.parent?.(),

      // and add a new one …
      backgroundColor: {
        default: null,
        parseHTML: element => element.getAttribute('data-background-color'),
        renderHTML: attributes => {
          return {
            'data-background-color': attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`
          }
        }
      }
    }
  }
})

export default {
  mixins: [lmInputMixin],
  components: {
    EditorContent,
    TableMenu,
    TextMenu,
    LmTreeSelect
  },
  props: {
    variablesOptions: {
      type: Array,
      default: () => []
    },
    useMentions: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    hideMenus: {
      type: Boolean,
      default: false
    },
    editorHeight: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default: 'Escribe un texto aquí...'
    },
    // Propiedad de contenido por retrocompatibilidad
    contentProps: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      editor: null,
      toggleTableSettings: false,
      toggleVariables: false
    }
  },
  computed: {
    data () {
      return this.value || this.contentProps || ''
    },
    editorClassList () {
      let classList = 'lm-editor-core'
      if (this.scrollable) {
        classList += ' scroll'
      }

      return classList
    }
  },
  watch: {
    scrollable (val) {
      this.editor.setOptions({
        editorProps: {
          attributes: {
            class: val ? 'lm-editor-core scroll' : 'lm-editor-core'
          }
        }
      })
    },
    disabled (val) {
      if (val) {
        this.editor.setEditable(false)
      } else {
        this.editor.setEditable(true)
      }
    },
    value (val) {
      const isSame = this.editor.getHTML() === val
      if (isSame) {
        return
      }
      this.editor.commands.setContent(val, false)
    }
  },
  methods: {
    onInsertVariable (event) {
      this.editor.commands.insertContent(`{{ ${event.value} }}`)
    },
    getEditorMentions (content) {
      const html = document.createElement('div')
      html.innerHTML = content
      const mentionsTags = html.querySelectorAll('[data-type="mention"]')

      return Array.from(mentionsTags).map(item => ({ id: item.dataset.id, name: item.dataset.label }))
    }
  },
  mounted () {
    const editorConfig = {
      editorProps: {
        attributes: {
          class: this.editorClassList
        }
      },
      extensions: [
        StarterKit,
        Table.configure({
          resizable: true
        }),
        TableRow,
        TableHeader,
        CustomTableCell,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right', 'justify']
        }),
        Placeholder.configure({
          placeholder: this.placeholder
        })
      ],
      content: this.data,
      onUpdate: () => {
        const htmlContent = this.editor.getHTML()
        const mentions = this.getEditorMentions(htmlContent)
        this.$emit('input', htmlContent)
        this.$emit('mentions-list', mentions)
        // Añadado este evento por razones de retrocompabilidad
        this.$emit('onUpdate', htmlContent)
      }
    }

    if (this.useMentions) {
      editorConfig.extensions.push(
        Mention.configure({
          HTMLAttributes: {
            class: 'mention'
          },
          suggestion
        })
      )
    }

    if (this.editorHeight) {
      editorConfig.editorProps.attributes = {
        class: this.editorClassList,
        style: `height: ${this.editorHeight}px; min-height: ${this.editorHeight}px;`
      }
    }

    this.editor = new Editor(editorConfig)

    if (this.disabled) {
      this.editor.setEditable(false)
    }
  },
  beforeDestroy () {
    this.editor.destroy()
  }
}
</script>

<style lang="sass">
  .lm-editor-core
    min-height: 300px
    padding: 1.5rem
    border: 1px solid #ced4da
    border-radius: 0.25rem

    &.scroll
      overflow-y: auto
      max-height: 300px
      &:hover
        &::-webkit-scrollbar-thumb
          border-radius: 20px
          background: #888
      &::-webkit-scrollbar
        width: 8px
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: transparent
      &::-webkit-scrollbar-thumb:hover
        background: #555

    &:focus
      outline: 0
      border-color: #80bdff
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25)

    ul li
      list-style: disc

  .ProseMirror
    margin: 1rem 0

    > * + *
      margin-top: 0.75em

    ul, ol
      padding: 0 1rem

    h1, h2, h3, h4, h5, h6
      line-height: 1.1

    pre
      background: #363636
      color: #fff
      font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace
      padding: 0.75rem 1rem
      border-radius: 0.5rem

      code
        color: inherit
        padding: 0
        background: none
        font-size: 1rem

    img
      max-width: 100%
      height: auto

    blockquote
      padding-left: 1rem
      border-left: 2px solid rgba(13, 13, 13, 0.1)

    hr
      border: none
      border-top: 2px solid rgba(13, 13, 13, 0.1)
      margin: 2rem 0

    p.is-editor-empty:first-child::before
      content: attr(data-placeholder)
      float: left
      color: #adb5bd
      pointer-events: none
      height: 0

    table
      border-collapse: collapse
      table-layout: fixed
      width: 100%
      margin: 0
      overflow: hidden

      td, th
        min-width: 1em
        border: 2px solid #ced4da
        padding: 3px 5px
        vertical-align: top
        box-sizing: border-box
        position: relative

      td > *
        margin-bottom: 0

      th
        > *
          margin-bottom: 0

        font-weight: bold
        text-align: left
        background-color: #f1f3f5

      .selectedCell:after
        z-index: 2
        position: absolute
        content: ""
        left: 0
        right: 0
        top: 0
        bottom: 0
        background: rgba(200, 200, 255, 0.4)
        pointer-events: none

      .column-resize-handle
        position: absolute
        right: -2px
        top: 0
        bottom: -2px
        width: 4px
        background-color: #adf
        pointer-events: none

      p
        margin: 0

  .tableWrapper
    overflow-x: auto

  .resize-cursor
    cursor: ew-resize
    cursor: col-resize

  .mention
    color: #007bff
</style>
